import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FaCoins, FaMoneyBillWave, FaHistory, FaCheckCircle, FaClock, FaTimes, FaHourglassHalf } from 'react-icons/fa';
import './RedeemCoins.css';

const RedeemCoins = () => {
  const user = useSelector((state) => state.user.user);
  const [coinsToRedeem, setCoinsToRedeem] = useState('');
  const [loading, setLoading] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [coinBalance, setCoinBalance] = useState({
    totalEarned: 0,
    totalRedeemed: 0,
    availableBalance: 0
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [approvedRedemptions, setApprovedRedemptions] = useState([]);

  // Fetch user's redemption history and coin balance
  useEffect(() => {
    if (user) {
      fetchRedemptions();
    }
  }, [user]);

  const fetchRedemptions = async () => {
    try {
      const response = await axios.get(`https://api.swasthmitra.com/api/redemption/user/${user._id}`);
      if (response.data.success) {
        setRedemptions(response.data.redemptions);
        setCoinBalance(response.data.coinBalance);
        
        // Check if there's a pending request
        const pending = response.data.redemptions.some(r => r.status === 'pending');
        setHasPendingRequest(pending);
        
        // Check for newly approved redemptions (since last visit)
        const lastVisitKey = `lastRedemptionCheck_${user._id}`;
        const lastVisit = localStorage.getItem(lastVisitKey);
        const currentTime = new Date().toISOString();
        
        if (lastVisit) {
          const newlyApproved = response.data.redemptions.filter(r => 
            r.status === 'approved' && 
            new Date(r.processedAt) > new Date(lastVisit)
          );
          
          if (newlyApproved.length > 0) {
            setApprovedRedemptions(newlyApproved);
            // Show alert for each newly approved redemption
            newlyApproved.forEach(redemption => {
              setTimeout(() => {
                alert(`🎉 Great news! Your redemption request for ${redemption.coinsRequested} coins has been approved! You can now collect your cash payment.`);
              }, 500);
            });
          }
        }
        
        // Update last visit time
        localStorage.setItem(lastVisitKey, currentTime);
      }
    } catch (error) {
      console.error('Error fetching redemptions:', error);
      setMessage({ type: 'error', text: 'Failed to load redemption history' });
    }
  };

  const handleRedeemSubmit = async (e) => {
    e.preventDefault();
    
    if (!coinsToRedeem || coinsToRedeem <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid number of coins' });
      return;
    }

    if (parseInt(coinsToRedeem) > coinBalance.availableBalance) {
      setMessage({ type: 'error', text: `Insufficient balance. Available: ${coinBalance.availableBalance} coins` });
      return;
    }

    if (hasPendingRequest) {
      setMessage({ type: 'error', text: 'You already have a pending redemption request' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('https://api.swasthmitra.com/api/redemption/create', {
        userId: user._id,
        coinsRequested: parseInt(coinsToRedeem)
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Redemption request submitted successfully!' });
        setCoinsToRedeem('');
        fetchRedemptions(); // Refresh the list
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to submit redemption request';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />;
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'cancelled':
        return <FaTimes className="status-icon cancelled" />;
      case 'delay':
        return <FaHourglassHalf className="status-icon delay" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status) => {
    return `status-badg ${status}`;
  };

  // Only show for employees
  if (user?.role !== 'employee') {
    return (
      <div className="redeem-coins-container">
        <div className="access-denied">
          <FaCoins className="access-icon" />
          <h3>Access Denied</h3>
          <p>Only employees can redeem coins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="redeem-coins-container">
      {/* Approved Redemptions Notification */}
      {approvedRedemptions.length > 0 && (
        <div className="approved-notifications">
          <div className="notification-header">
            <FaCheckCircle className="notification-icon success" />
            <h3>Congratulations! 🎉</h3>
          </div>
          <div className="notification-content">
            <p>Your following redemption requests have been approved:</p>
            {approvedRedemptions.map((redemption) => (
              <div key={redemption._id} className="approved-item">
                <div className="approved-details">
                  <span className="coins-amount">
                    <FaCoins className="coin-icon" />
                    {redemption.coinsRequested} coins
                  </span>
                  <span className="approved-date">
                    Approved on {new Date(redemption.processedAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="cash-message">
                  You can now collect your cash payment! Please contact the admin.
                </p>
                {redemption.adminNotes && (
                  <p className="admin-notes">
                    <strong>Admin Notes:</strong> {redemption.adminNotes}
                  </p>
                )}
              </div>
            ))}
            <button 
              onClick={() => setApprovedRedemptions([])} 
              className="acknowledge-btn"
            >
              Acknowledge
            </button>
          </div>
        </div>
      )}
      <div className="balance-card">
        <div className="balance-header">
          <FaCoins className="balance-icon" />
          <h3>Coin Balance</h3>
        </div>
        <div className="balance-stats">
          <div className="balance-item">
            <span className="balance-label">Total Earned:</span>
            <span className="balance-value">{coinBalance.totalEarned}</span>
          </div>
          <div className="balance-item">
            <span className="balance-label">Total Redeemed:</span>
            <span className="balance-value">{coinBalance.totalRedeemed}</span>
          </div>
          <div className="balance-item main-balance">
            <span className="balance-label">Available Balance:</span>
            <span className="balance-value highlight">{coinBalance.availableBalance}</span>
          </div>
        </div>
      </div>

      {/* Redemption Form */}
      <div className="redemption-form-card">
        <div className="form-header">
          <FaMoneyBillWave className="form-icon" />
          <h3>Redeem Coins</h3>
        </div>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleRedeemSubmit} className="redemption-form">
          <div className="input-group">
            <label htmlFor="coinsToRedeem">Enter coins to redeem:</label>
            <input
              type="number"
              id="coinsToRedeem"
              value={coinsToRedeem}
              onChange={(e) => setCoinsToRedeem(e.target.value)}
              placeholder="Enter number of coins"
              min="1"
              max={coinBalance.availableBalance}
              disabled={loading || hasPendingRequest}
              className="coin-input"
            />
            <small className="input-help">
              Maximum: {coinBalance.availableBalance} coins
            </small>
          </div>
          
          <button 
            type="submit" 
            disabled={loading || hasPendingRequest || coinBalance.availableBalance === 0}
            className="redeem-button"
          >
            {loading ? 'Processing...' : 'Submit Redemption Request'}
          </button>
          
          {hasPendingRequest && (
            <p className="pending-notice">
              <FaClock className="notice-icon" />
              You have a pending redemption request. Please wait for it to be processed.
            </p>
          )}
        </form>
      </div>

      {/* Redemption History */}
      <div className="redemption-history-card">
        <div className="history-header">
          <FaHistory className="history-icon" />
          <h3>Redemption History</h3>
        </div>
        
        {redemptions.length > 0 ? (
          <div className="redemption-list">
            {redemptions.map((redemption) => (
              <div key={redemption._id} className="redemption-item">
                <div className="redemption-main">
                  <div className="redemption-info">
                    <div className="redemption-amount">
                      <FaCoins className="coin-icon" />
                      <span className="amount">{redemption.coinsRequested} coins</span>
                    </div>
                    <div className="redemption-date">
                      {new Date(redemption.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  <div className="redemption-status">
                    {getStatusIcon(redemption.status)}
                    <span className={getStatusClass(redemption.status)}>
                      {redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                {(redemption.adminNotes || redemption.rejectionReason) && (
                  <div className="redemption-notes">
                    {redemption.rejectionReason && (
                      <p className="rejection-reason">
                        <strong>Reason:</strong> {redemption.rejectionReason}
                      </p>
                    )}
                    {redemption.adminNotes && (
                      <p className="admin-notes">
                        <strong>Admin Notes:</strong> {redemption.adminNotes}
                      </p>
                    )}
                  </div>
                )}
                
                {redemption.processedAt && (
                  <div className="processed-info">
                    <small>
                      Processed on {new Date(redemption.processedAt).toLocaleDateString()}
                      {redemption.processedBy && ` by ${redemption.processedBy.name}`}
                    </small>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-redemptions">
            <p>No redemption requests yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedeemCoins;